// frontend/src/ExplorerLogModal.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import Graph from 'react-graph-vis'; // Assuming react-graph-vis is installed
import './ExplorerLogModal.css'; // Create this CSS file

// --- Reusable Collapsible List (copied from AppExplorer for self-containment) ---
function CollapsibleList({ title, items, limit = 20, initiallyExpanded = false }) {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
    if (!items || items.length === 0) { return (<div className="collapsible-list empty"><h5>{title} (0)</h5></div>); }
    const toggleExpand = () => setIsExpanded(!isExpanded);
    const displayedItems = isExpanded ? items : items.slice(0, limit);
    return (
        <div className="collapsible-list">
            <h5 onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                {title} ({items.length}) {items.length > limit && (isExpanded ? ' [-]' : ' [+]')}
            </h5>
            {isExpanded && (<><ul>{displayedItems.map((item, index) => (<li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>))}</ul>{items.length > limit && (<button onClick={toggleExpand} className="toggle-expand">Show Less</button>)}</>)}
            {!isExpanded && items.length > limit && (<button onClick={toggleExpand} className="toggle-expand">Show More ({items.length - limit} more)</button>)}
            {!isExpanded && items.length <= limit && displayedItems.length > 0 && (<ul>{displayedItems.map((item, index) => (<li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>))}</ul>)}
        </div>
    );
}
// --- End Collapsible List ---

// --- Modal Style (full-screen friendly, stays above top bar) ---
const customModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: '95vw',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden', // internal areas manage their own scroll
        padding: '0',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        backgroundColor: '#fff',
        zIndex: 10051,
        display: 'flex',
        flexDirection: 'column'
    },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 10050 },
};
// --- Nested (Article/Statements) Modal Style ---
const nestedModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: '70vw', maxWidth: '900px', minWidth: '600px',
        maxHeight: '80vh',
        padding: '0',
        border: '1px solid var(--dark-border-primary, #40444b)',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, var(--dark-bg-secondary, #2b2d31), var(--dark-bg-tertiary, #232428))',
        boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        zIndex: 10070
    },
    overlay: { backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 10069 }
};
// --- End Modal Style ---

const ExplorerLogModal = ({
    isOpen,
    onClose,
    logData, // Expects the full object returned by /api/load_log/
    isLoading,
    error
}) => {
    const [activeStepIndex, setActiveStepIndex] = useState(-1);
    const [isAutoPlayingGraph, setIsAutoPlayingGraph] = useState(false);
    const autoPlayIntervalRef = useRef(null);
    const graphAreaRef = useRef(null);
    const networkRef = useRef(null);
    // --- Article / Statement Modal State (added) ---
    const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
    const [selectedNodeInfoForModal, setSelectedNodeInfoForModal] = useState(null);
    const [modalContent, setModalContent] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);

    // Set initial active step when data loads
    useEffect(() => {
        if (logData?.displayDataHistory?.length > 0) {
            setActiveStepIndex(logData.displayDataHistory.length - 1); // Start at the last step
        } else {
            setActiveStepIndex(-1);
        }
        // Stop autoplay if modal re-opens or data changes
        setIsAutoPlayingGraph(false);
        if (autoPlayIntervalRef.current) {
             clearInterval(autoPlayIntervalRef.current);
             autoPlayIntervalRef.current = null;
        }
    }, [logData]); // Dependency on logData

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
            }
        };
    }, []);


    // --- Graph Visualization Helpers (adapted from AppExplorer) ---
     const graphOptions = { /* ... options remain the same ... */
        layout: { hierarchical: false },
        physics: { solver: 'forceAtlas2Based', forceAtlas2Based: { gravitationalConstant: -55, centralGravity: 0.015, springLength: 110, springConstant: 0.08, damping: 0.5, avoidOverlap: 0.6 }, stabilization: { enabled: true, iterations: 150, updateInterval: 25 } },
        edges: { color: { color: "#848484", highlight: '#ff0000', hover: '#d3d3d3' }, arrows: { to: { enabled: true, scaleFactor: 0.6 } }, font: { size: 9, color: '#a0a0a0', strokeWidth: 0 }, smooth: { enabled: true, type: "continuous", roundness: 0.5 }, width: 1, hoverWidth: 1.5 },
        nodes: { shape: 'dot', size: 12, borderWidth: 1.5, font: { size: 11, color: '#333' }, color: { border: '#666', background: '#D2E5FF', highlight: { border: '#2B7CE9', background: '#87CEFA' }, hover: { border: '#2B7CE9', background: '#ADD8E6' } } },
        interaction: { tooltipDelay: 150, hover: true, navigationButtons: false, /* Disable built-in nav */ keyboard: true, zoomView: true, dragView: true },
        height: '100%', width: '100%'
    };

    // Fit/redraw graph to container when modal opens, step changes, or container resizes
    useEffect(() => {
        if (!isOpen || !networkRef.current) return;
        try {
            networkRef.current.redraw();
            networkRef.current.fit({ animation: false, maxZoomLevel: 1.2 });
        } catch (_) {}
    }, [isOpen, activeStepIndex]);

    useEffect(() => {
        if (!graphAreaRef.current || !networkRef.current) return;
        let ro;
        try {
            ro = new ResizeObserver(() => {
                try {
                    networkRef.current.redraw();
                } catch (_) {}
            });
            ro.observe(graphAreaRef.current);
        } catch (_) {}
        return () => {
            try { ro && ro.disconnect(); } catch (_) {}
        };
    }, [graphAreaRef.current, networkRef.current]);

     const getGraphDataForStep = useCallback((stepIndex) => {
        // ... (logic copied directly from AppExplorer's getGraphDataForStep) ...
         if (!logData || !logData.displayDataHistory || stepIndex < 0 || stepIndex >= logData.displayDataHistory.length) {
             return { nodes: [], edges: [], nodeLabelMap: {} };
         }
         const stepData = logData.displayDataHistory[stepIndex];
         if (!stepData || !stepData.graphStructure) {
            console.warn(`Modal: No valid graph data found for step index ${stepIndex}`);
            return { nodes: [], edges: [], nodeLabelMap: {} };
         }
         const structure = stepData.graphStructure || { nodes: [], edges: [] };
         const highlights = stepData.graphHighlights || { currentNodes: [], connectedNodes: [] };

         const nodes = (structure.nodes || []).map(node => {
              if (!node || typeof node.id === 'undefined') return null;
              let extraStyle = {};
              if ((highlights.currentNodes || []).includes(node.id)) extraStyle = { color: { background: '#FFBDBD', border: '#FF0000' }, size: 20, shape: 'star', font: { color: '#A52A2A', size: 13, weight: 'bold' }, borderWidth: 2 };
              else if ((highlights.connectedNodes || []).includes(node.id)) extraStyle = { color: { background: '#FFEBCD', border: '#FFA500' }, size: 16, shape: 'diamond', borderWidth: 1.5, font: { size: 12 } };
              return { ...node, label: String(node.label ?? ''), ...extraStyle };
          }).filter(Boolean);

         const edges = (structure.edges || []).map(edge => {
              if (!edge || typeof edge.from === 'undefined' || typeof edge.to === 'undefined') return null;
              const isFromCurrent = (highlights.currentNodes || []).includes(edge.from);
              const isToCurrent = (highlights.currentNodes || []).includes(edge.to);
              let edgeStyle = {};
              if (isFromCurrent || isToCurrent) edgeStyle = { color: { color: '#FF6347', highlight: '#FF0000' }, width: 2.5 };
              // Keep articleId and title if they exist in the data
              return { ...edge, articleId: edge.articleId, title: edge.title, ...edgeStyle };
          }).filter(Boolean);

          const nodeLabelMap = nodes.reduce((acc, node) => { if (node) acc[node.id] = node.label; return acc; }, {});
         return { nodes, edges, nodeLabelMap };
    }, [logData]); // Depends only on logData

    const activeGraphData = getGraphDataForStep(activeStepIndex);

    // --- Step Navigation Handlers (adapted from AppExplorer) ---
    const handlePreviousStep = () => { if (isAutoPlayingGraph) return; setActiveStepIndex(prev => Math.max(0, prev - 1)); };
    const handleNextStep = () => { if (isAutoPlayingGraph) return; setActiveStepIndex(prev => Math.min((logData?.displayDataHistory?.length || 1) - 1, prev + 1)); };
    const handleToggleAutoPlayGraph = () => {
         if (isAutoPlayingGraph) {
            setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
        } else {
             const historyLength = logData?.displayDataHistory?.length || 0;
             if (historyLength <= 1) return;
             setIsAutoPlayingGraph(true);
             let startIndex = activeStepIndex >= 0 && activeStepIndex < historyLength - 1 ? activeStepIndex : 0;
             setActiveStepIndex(startIndex); // Ensure starting from a valid index or 0
             autoPlayIntervalRef.current = setInterval(() => {
                setActiveStepIndex(prev => {
                    const nextIndex = prev + 1;
                     if (nextIndex >= historyLength) {
                        setIsAutoPlayingGraph(false); clearInterval(autoPlayIntervalRef.current); autoPlayIntervalRef.current = null;
                        return prev; // Stay at the last step
                     }
                    return nextIndex;
                });
            }, 3000);
        }
    };

    const canGoPrevious = activeStepIndex > 0;
    const canGoNext = activeStepIndex < (logData?.displayDataHistory?.length || 1) - 1;
    const activeStepData = (logData?.displayDataHistory && activeStepIndex >= 0 && activeStepIndex < logData.displayDataHistory.length)
        ? logData.displayDataHistory[activeStepIndex]
        : null;

    // --- Node Click Handler (opens statements/article modal) ---
    const handleGraphClick = useCallback((event) => {
        const { nodes: clickedNodeIds } = event;
        if (!clickedNodeIds || clickedNodeIds.length === 0) return;
        const clickedNodeId = clickedNodeIds[0];
        const currentGraphData = getGraphDataForStep(activeStepIndex);
        const nodeLabel = currentGraphData?.nodeLabelMap?.[clickedNodeId] ?? 'Unknown Node';
        const connectedEdges = (currentGraphData?.edges || []).filter(edge =>
            (edge.from === clickedNodeId || edge.to === clickedNodeId) && edge.articleId && edge.articleId !== 'N/A'
        );
        const statements = connectedEdges.map(edge => ({
            statement: edge.title || `Edge from ${edge.from} to ${edge.to}`,
            articleId: edge.articleId
        }));
        setSelectedNodeInfoForModal({ nodeId: clickedNodeId, nodeLabel, connectedStatements: statements });
        setModalContent(null); setIsModalLoading(false); setIsArticleModalOpen(true);
    }, [activeStepIndex, getGraphDataForStep]);

    // --- Article Modal Helpers ---
    const closeArticleModal = () => { setIsArticleModalOpen(false); setModalContent(null); setSelectedNodeInfoForModal(null); setIsModalLoading(false); };
    const handleModalBackToList = () => { setModalContent(null); setIsModalLoading(false); };
    const fetchArticleDetailsForModal = useCallback(async (articleId) => {
        if (!articleId || articleId === 'N/A') { setModalContent({ error: `Invalid Article ID: ${articleId}` }); return; }
        setIsModalLoading(true); setModalContent(null);
        try {
            const response = await fetch(`/api/files/get_file_metadata_by_id/${articleId}`, { credentials: 'include' });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || `HTTP error ${response.status}`);
            }
            setModalContent(data);
        } catch (err) {
            setModalContent({ error: `Failed to load details for Article ID ${articleId}: ${err.message}` });
        } finally {
            setIsModalLoading(false);
        }
    }, []);


    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={customModalStyles}
            contentLabel="Knowledge Exploration Details"
            ariaHideApp={false}
        >
            {/* Modal Header */}
            <div className="explorer-modal-header">
                <h2>Exploration Details: {logData?.conversationName || logData?.uniqueId?.substring(0, 8) || 'N/A'}</h2>
                <button onClick={onClose} className="modal-close-button top-right">×</button>
            </div>

            {/* Loading/Error State */}
            {isLoading && <div className="loading-fullpage">Loading Log Data...</div>}
            {!isLoading && error && <div className="error" style={{ margin: '20px' }}>Error loading log: {error}</div>}
            {!isLoading && !error && !logData && <div className="loading-fullpage">No log data available.</div>}

            {/* Main Content Area */}
            {!isLoading && !error && logData && (
                <div className="explorer-modal-content">
                    {/* Left Column: Step Details */}
                    <div className="explorer-modal-left">
                        {logData.initialQuery && <p className="initial-query"><strong>Initial Query:</strong> {logData.initialQuery}</p>}
                        <h3 className="step-details-title">Step Details ({activeStepIndex + 1} / {logData.displayDataHistory?.length || 0})</h3>

                        {activeStepData ? (
                            <div className="active-chat-step-details">
                                {/* Use CollapsibleList for cleaner display */}
                                {activeStepData.stepCurrentNodes?.length >= 0 && <CollapsibleList title="Nodes Explored" items={activeStepData.stepCurrentNodes || []} limit={10} initiallyExpanded={false} />}
                                {activeStepData.stepNewlySelectedStatements?.length >= 0 && <CollapsibleList title="Statements Added" items={activeStepData.stepNewlySelectedStatements || []} limit={10} initiallyExpanded={false} />}
                                {activeStepData.stepConnectedStatements?.length >= 0 && <CollapsibleList title="Connected Statements Found" items={activeStepData.stepConnectedStatements || []} limit={15} initiallyExpanded={false} />}
                                {Array.isArray(activeStepData.stepRagContext) && activeStepData.stepRagContext.length > 0 && <CollapsibleList title="RAG Context (This Step)" items={activeStepData.stepRagContext} limit={5} initiallyExpanded={false} />}

                                {/* LLM Response & Reasoning in Details */}
                                {activeStepData.stepLlmResponse && (
                                    <details className="step-details-toggle">
                                        <summary>LLM Analysis (JSON)</summary>
                                        <pre>{JSON.stringify(activeStepData.stepLlmResponse, null, 2)}</pre>
                                    </details>
                                )}
                                {activeStepData.stepReasoning && (
                                    <details className="step-details-toggle">
                                        <summary>LLM Reasoning</summary>
                                        <pre style={{ whiteSpace: 'pre-wrap' }}>{activeStepData.stepReasoning}</pre>
                                    </details>
                                )}
                                 {activeStepData.stepFinalResponse && activeStepIndex === logData.displayDataHistory.length - 1 && (
                                    <details className="step-details-toggle" open>
                                        <summary>Final Synthesized Response</summary>
                                        <pre className="final-response">{activeStepData.stepFinalResponse}</pre>
                                    </details>
                                )}
                            </div>
                        ) : (
                            <p>Select a step using the graph controls.</p>
                        )}
                    </div>

                    {/* Right Column: Graph */}
                    <div className="explorer-modal-right">
                        <div className="graph-container-modal">
                             <div className="graph-header-modal">
                                  <span>Graph Visualization (Step {activeStepIndex + 1})</span>
                                  <div className="step-nav-buttons-modal">
                                     {(logData?.displayDataHistory?.length || 0) > 1 && (
                                         <button onClick={handleToggleAutoPlayGraph} disabled={isLoading} className={`autoplay-button ${isAutoPlayingGraph ? 'active' : ''}`}> {isAutoPlayingGraph ? 'Pause' : 'Play'} </button>
                                     )}
                                    <button onClick={handlePreviousStep} disabled={!canGoPrevious || isAutoPlayingGraph}>Prev</button>
                                    <button onClick={handleNextStep} disabled={!canGoNext || isAutoPlayingGraph}>Next</button>
                                  </div>
                             </div>
                             <div className="graph-vis-area" ref={graphAreaRef}>
                                {activeGraphData && activeGraphData.nodes && activeGraphData.nodes.length > 0 ? (
                                    <Graph
                                        key={`modal-graph-step-${activeStepIndex}-${logData.uniqueId}`} // More specific key
                                        graph={activeGraphData}
                                        options={graphOptions}
                                        getNetwork={(nw) => { networkRef.current = nw; }}
                                        style={{ height: '100%', width: '100%' }}
                                            events={{ click: handleGraphClick }}
                                    />
                                ) : ( <div className="graph-placeholder"> {activeStepIndex < 0 ? "Select a step." : "No graph data for this step."} </div> )}
                             </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Footer */}
            <div className="explorer-modal-footer">
                <button onClick={onClose} className="modal-button secondary">Close</button>
            </div>

            {/* Article / Statement Details Modal (nested) */}
            <Modal
                isOpen={isArticleModalOpen}
                onRequestClose={closeArticleModal}
                style={nestedModalStyles}
                contentLabel="Node Information"
                ariaHideApp={false}
            >
                <div className="nested-article-modal-header">
                    <h2>{modalContent ? 'Article Details' : 'Node Statements'}</h2>
                    <div className="nested-header-actions">
                        {modalContent && selectedNodeInfoForModal && !modalContent?.error && (
                            <button onClick={handleModalBackToList} className="pill-button neutral" title="Back to statements list">← Back</button>
                        )}
                        <button onClick={closeArticleModal} className="pill-button danger" title="Close">×</button>
                    </div>
                </div>
                <div className="nested-modal-content">
                    {isModalLoading && <div className="loading centered">Loading article details...</div>}
                    {!isModalLoading && modalContent ? (
                        <div className="article-details-modal scroll-area">
                            {modalContent.error ? ( <p className="error">{modalContent.error}</p> ) : (
                                <>
                                    <div className="meta-grid">
                                        <div><span className="meta-label">ID</span><span className="meta-value">{modalContent.id}</span></div>
                                        <div><span className="meta-label">File</span><span className="meta-value">{modalContent.file_name || 'N/A'}</span></div>
                                        <div><span className="meta-label">Topic</span><span className="meta-value">{modalContent.topic_title || 'N/A'}</span></div>
                                    </div>
                                    {modalContent.title_summary && (
                                        <section className="section-block" aria-label="Summary">
                                            <h3>Summary</h3>
                                            <p>{modalContent.title_summary}</p>
                                        </section>
                                    )}
                                    {modalContent.key_concepts && (
                                        <section className="section-block" aria-label="Key Concepts">
                                            <h3>Key Concepts</h3>
                                            <pre className="code-block small">{modalContent.key_concepts}</pre>
                                        </section>
                                    )}
                                    {modalContent.text && (
                                        <section className="section-block" aria-label="Full Text">
                                            <h3>Full Text</h3>
                                            <pre className="code-block markdown-text">{modalContent.text}</pre>
                                        </section>
                                    )}
                                </>
                            )}
                        </div>
                    ) : !isModalLoading && selectedNodeInfoForModal ? (
                        <div className="statement-list-modal scroll-area">
                            <h3 className="subtitle">Statements for: <span className="node-label">{selectedNodeInfoForModal.nodeLabel}</span></h3>
                            {selectedNodeInfoForModal.connectedStatements.length > 0 ? (
                                <ul className="statement-list">
                                    {selectedNodeInfoForModal.connectedStatements.map((item, index) => (
                                        <li key={index} className="statement-item">
                                            <div className="statement-text" title={item.statement}>{item.statement}</div>
                                            <button
                                                onClick={() => fetchArticleDetailsForModal(item.articleId)}
                                                className="pill-button primary"
                                                title={`View details for Article ID: ${item.articleId}`}
                                                disabled={!item.articleId || item.articleId === 'N/A'}
                                            >
                                                View Details
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="empty-state">No statements with associated Article IDs found.</p>
                            )}
                        </div>
                    ) : (
                        <div className="loading centered">Loading content...</div>
                    )}
                </div>
                <div className="nested-modal-footer">
                    <button onClick={closeArticleModal} className="pill-button neutral">Close</button>
                </div>
            </Modal>
        </Modal>
    );
};

export default ExplorerLogModal;